import { Fragment, type ReactNode, useRef } from "react";
import { useFormContext } from "react-hook-form";

import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  useComboboxFilteredItems,
} from "../primitives/combobox";
import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from "../primitives/form";
import { buildComboboxFilter } from "./colocaCombobox.shared";

// --- Public types ---

/** A single selectable option. */
export type ComboboxOption = {
  /** Value stored in the form. Must match the string expected by the Zod schema. */
  value: string;
  /** Text shown in the dropdown and used for default filtering. */
  label: string;
  disabled?: boolean;
};

/** A labeled group of related options rendered under a section header. */
export type ComboboxOptionGroup = {
  /** Section header shown above the group's items. */
  label: string;
  items: ComboboxOption[];
};

// --- Prop types (V1: items XOR groups; V2: multiple discriminant) ---

type WithItems = { items: ComboboxOption[]; groups?: never };
type WithGroups = { groups: ComboboxOptionGroup[]; items?: never };

type ComboboxBaseProps = (WithItems | WithGroups) & {
  /** Field name matching the Zod schema key. Must be registered in the parent `useForm`. */
  name: string;
  /** Visible label rendered above the combobox. */
  label?: ReactNode;
  /** Helper text rendered below. Use for format hints — do not repeat the label. */
  description?: ReactNode;
  /** Placeholder shown in the text input. Defaults to `"Search…"`. */
  placeholder?: string;
  /** Text rendered when no items match the query. Defaults to `"No results."`. */
  emptyMessage?: string;
  disabled?: boolean;
  /**
   * Marks the field as required: an asterisk on the label and `aria-required` on the input. Presentational —
   * the Zod schema still owns validation.
   */
  required?: boolean;
  /**
   * Override the default case-insensitive substring filter. Receives the item's `value` string and the
   * current query; return `true` to show the item. Pass `null` when `items` are already the result of a
   * server-side search — filtering them again in the browser can only hide rows the server returned.
   */
  filter?: ((itemValue: string, query: string) => boolean) | null;
  /**
   * Custom render for each item body. Receives the full `ComboboxOption`. When omitted, `option.label` is
   * rendered.
   */
  renderItem?: (option: ComboboxOption) => ReactNode;
  className?: string;
};

type ColocaComboboxFieldSingleProps = ComboboxBaseProps & {
  multiple?: false;
  /** Fires after the form value updates — hook for dependent fields (e.g. cascading selects). */
  onValueChange?: (value: string | null) => void;
};
type ColocaComboboxFieldMultiProps = ComboboxBaseProps & {
  multiple: true;
  /** Fires after the form value updates — hook for dependent fields (e.g. cascading selects). */
  onValueChange?: (value: string[]) => void;
};

export type ColocaComboboxFieldProps = ColocaComboboxFieldSingleProps | ColocaComboboxFieldMultiProps;

// --- Internal helpers ---

function flattenOptions(items?: ComboboxOption[], groups?: ComboboxOptionGroup[]): ComboboxOption[] {
  return [...(items ?? []), ...(groups ?? []).flatMap((g) => g.items)];
}

// --- List renderers (must be named components so hooks are called at the top level) ---

// Flat items: ComboboxCollection reads filteredItems from root context.
function FlatItemsList({ renderItem }: { renderItem?: (option: ComboboxOption) => ReactNode }) {
  return (
    <ComboboxList>
      <ComboboxCollection>
        {(item: ComboboxOption) => (
          <ComboboxItem value={item.value} disabled={item.disabled}>
            {renderItem ? renderItem(item) : item.label}
          </ComboboxItem>
        )}
      </ComboboxCollection>
    </ComboboxList>
  );
}

// Grouped items: useComboboxFilteredItems returns the already-filtered groups from root context.
// ComboboxGroup items={filteredGroup.items} passes only the matching items to ComboboxCollection.
function GroupedItemsList({ renderItem }: { renderItem?: (option: ComboboxOption) => ReactNode }) {
  const filteredGroups = useComboboxFilteredItems<ComboboxOptionGroup>();
  return (
    <ComboboxList>
      {filteredGroups.map((group: ComboboxOptionGroup, i: number) => (
        <Fragment key={group.label}>
          {i > 0 && <ComboboxSeparator />}
          <ComboboxGroup items={group.items}>
            <ComboboxLabel>{group.label}</ComboboxLabel>
            <ComboboxCollection>
              {(item: ComboboxOption) => (
                <ComboboxItem value={item.value} disabled={item.disabled}>
                  {renderItem ? renderItem(item) : item.label}
                </ComboboxItem>
              )}
            </ComboboxCollection>
          </ComboboxGroup>
        </Fragment>
      ))}
    </ComboboxList>
  );
}

// --- Internal sub-components (rendered inside FormField context; useFormField() is safe here) ---

type SingleProps = {
  value: string | null;
  onValueChange: (val: string | null) => void;
  onBlur: () => void;
  items?: ComboboxOption[];
  groups?: ComboboxOptionGroup[];
  placeholder: string;
  emptyMessage: string;
  disabled?: boolean;
  filterFn: ((item: string, query: string) => boolean) | null | undefined;
  renderItem?: (option: ComboboxOption) => ReactNode;
};

function SingleCombobox({
  value,
  onValueChange,
  onBlur,
  items,
  groups,
  placeholder,
  emptyMessage,
  disabled,
  filterFn,
  renderItem,
}: SingleProps) {
  // This field sets its own aria attributes instead of going through `FormControl`, so `aria-required` has
  // to be read from the form item here too — otherwise the label would show the asterisk and the input
  // would not announce it.
  const { formItemId, formDescriptionId, formMessageId, error, required } = useFormField();
  const rootItems = items ?? groups;
  const allOptions = flattenOptions(items, groups);
  // Lets the dropdown hide itself while the field is scrolled out of view (e.g. inside a scrollable dialog),
  // instead of floating over whatever is on top. Positioning is untouched.
  const fieldRef = useRef<HTMLInputElement | null>(null);

  return (
    <Combobox
      value={value}
      onValueChange={onValueChange}
      onOpenChange={(open) => {
        if (!open) onBlur();
      }}
      disabled={disabled}
      items={rootItems}
      filter={filterFn}
      itemToStringLabel={(v: string) => allOptions.find((o) => o.value === v)?.label ?? v}
    >
      <ComboboxInput
        ref={fieldRef}
        id={formItemId}
        aria-describedby={error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId}
        aria-invalid={!!error || undefined}
        aria-required={required || undefined}
        placeholder={placeholder}
        showClear
        showTrigger
        disabled={disabled}
      />
      <ComboboxContent clipAnchor={fieldRef}>
        {items ? <FlatItemsList renderItem={renderItem} /> : <GroupedItemsList renderItem={renderItem} />}
        <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
      </ComboboxContent>
    </Combobox>
  );
}

type MultiProps = {
  value: string[];
  onValueChange: (val: string[]) => void;
  onBlur: () => void;
  allOptions: ComboboxOption[];
  items?: ComboboxOption[];
  groups?: ComboboxOptionGroup[];
  emptyMessage: string;
  disabled?: boolean;
  filterFn: ((item: string, query: string) => boolean) | null | undefined;
  renderItem?: (option: ComboboxOption) => ReactNode;
};

function MultiCombobox({
  value,
  onValueChange,
  onBlur,
  allOptions,
  items,
  groups,
  emptyMessage,
  disabled,
  filterFn,
  renderItem,
}: MultiProps) {
  const { formItemId, formDescriptionId, formMessageId, error, required } = useFormField();
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const rootItems = items ?? groups;

  return (
    <Combobox
      multiple
      value={value}
      onValueChange={onValueChange}
      onOpenChange={(open) => {
        if (!open) onBlur();
      }}
      disabled={disabled}
      items={rootItems}
      filter={filterFn}
    >
      <div ref={anchorRef}>
        <ComboboxChips>
          {value.map((val) => {
            const option = allOptions.find((o) => o.value === val);
            if (!option) return null;
            return <ComboboxChip key={val}>{option.label}</ComboboxChip>;
          })}
          <ComboboxChipsInput
            id={formItemId}
            aria-describedby={error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId}
            aria-invalid={!!error || undefined}
            aria-required={required || undefined}
          />
        </ComboboxChips>
      </div>
      <ComboboxContent anchor={anchorRef}>
        {items ? <FlatItemsList renderItem={renderItem} /> : <GroupedItemsList renderItem={renderItem} />}
        <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
      </ComboboxContent>
    </Combobox>
  );
}

// --- Public component ---

/**
 * Controlled combobox (searchable select) wired to the nearest React Hook Form context.
 *
 * Use inside a `<ColocaForm>` for any field where users need to filter a list before selecting. Pass a flat
 * list via `items` or a categorized list via `groups` — never both. For multi-value selection use `multiple`.
 * Custom item rendering is available via `renderItem`. Validation errors from the Zod schema appear
 * automatically via `<FormMessage>`.
 */
export function ColocaComboboxField(props: ColocaComboboxFieldProps) {
  const {
    name,
    label,
    description,
    placeholder = "Search…",
    emptyMessage = "No results.",
    disabled,
    required,
    filter: customFilter,
    renderItem,
    className,
    multiple,
  } = props;

  const items = props.items;
  const groups = props.groups;
  // Narrow the variant-specific callback through the `multiple` discriminant.
  const onSingleValueChange = props.multiple ? undefined : props.onValueChange;
  const onMultiValueChange = props.multiple ? props.onValueChange : undefined;

  const { control } = useFormContext();
  const allOptions = flattenOptions(items, groups);
  const filterFn = buildComboboxFilter(customFilter);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className} required={required}>
          {label && <FormLabel>{label}</FormLabel>}
          {multiple ? (
            <MultiCombobox
              value={(field.value as string[]) ?? []}
              onValueChange={(val) => {
                field.onChange(val);
                onMultiValueChange?.(val);
              }}
              onBlur={field.onBlur}
              allOptions={allOptions}
              items={items}
              groups={groups}
              emptyMessage={emptyMessage}
              disabled={disabled}
              filterFn={filterFn}
              renderItem={renderItem}
            />
          ) : (
            <SingleCombobox
              value={(field.value as string | undefined) || null}
              onValueChange={(val) => {
                field.onChange(val ?? "");
                onSingleValueChange?.(val);
              }}
              onBlur={field.onBlur}
              items={items}
              groups={groups}
              placeholder={placeholder}
              emptyMessage={emptyMessage}
              disabled={disabled}
              filterFn={filterFn}
              renderItem={renderItem}
            />
          )}
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
